import './JsonPreview.css'
import { defineDataType, JsonViewer, nullType, stringType } from '@textea/json-viewer'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { PreferencesContext } from './PreferencesContext'
import { isNumber, updateValueAtPath } from '../utils'

interface JsonPreviewParams {
    value: Record<string | number, any>
    onChange?: (value: Record<string | number, any>, patch: Record<string | number, any>) => void
    editable?: (path: Array<string | number>) => boolean
    addable?: (path: Array<string | number>) => boolean
}

export default function JsonPreview({ value, onChange, editable = () => false, addable }: JsonPreviewParams) {
    const { t } = useTranslation()
    const [preferences] = useContext(PreferencesContext)

    const nullTypeOverride = defineDataType<null>({
        ...nullType,
        serialize() {
            return ''
        },
        deserialize(value) {
            if (value === '') return null
            if (isNumber(value)) return Number(value)
            if (value === 'true') return true
            if (value === 'false') return false
            return value as any
        },
        Editor: stringType.Editor,
    })

    const phoneType = defineDataType({
        is: (value, path) => {
            if (typeof value !== 'string') return false
            if (path[0] === 'phone') return true
            return false
        },
        Component: stringType.Component,
        Editor: stringType.Editor,
        deserialize(value) {
            return value.replace(/\s/g, '')
        },
        serialize(value) {
            return value.replace(/\s/g, '')
        },
    })

    return (
        <JsonViewer
            className="json-viewer"
            value={value}
            rootName={false}
            theme={preferences.mode}
            editable={editable}
            onChange={(path, _, newValue) => {
                const { updated, patch } = updateValueAtPath(value, path, newValue)
                onChange?.(updated, patch)
            }}
            enableDelete={editable}
            onDelete={(path) => {
                const { updated, patch } = updateValueAtPath(value, path, null)
                onChange?.(updated, patch)
            }}
            enableAdd={addable}
            onAdd={addable
                ? (path) => {
                    const parent = path.reduce<any>((node, key) => node?.[key], value)
                    let fieldPath: Array<string | number>
                    if (Array.isArray(parent)) {
                        fieldPath = [...path, parent.length]
                    } else {
                        const key = window.prompt(t('add_field_name'))?.trim()
                        if (!key) return
                        fieldPath = [...path, key]
                    }
                    if (!addable(fieldPath)) return
                    const fieldValue = window.prompt(t('add_field_value'))
                    if (fieldValue === null) return
                    const { updated, patch } = updateValueAtPath(value, fieldPath, fieldValue)
                    onChange?.(updated, patch)
                }
                : undefined}
            valueTypes={[phoneType, nullTypeOverride]}
        />
    )
}
